/**
 * 請求・プラン（05-ui.md §8.9）。
 *
 * 支払い遅延は受信を止めない（送信だけ止める）という設計なので、
 * 「何がいつ止まるのか」を曖昧にせず具体的に書く。
 *
 * この画面が持つ責務は 5 つ:
 *   1. 契約中のプランと**今月の見込み額**（`upcomingInvoice`）
 *   2. **支払い方法の登録**（環境が `manual` ならカードのボタンを出さない）
 *   3. **解約と撤回**（特商法表記の「コントロールパネルからいつでも解約」の実体）
 *   4. 請求先情報（請求書の宛先）の不足の告知
 *   5. 請求履歴と**請求書 PDF のダウンロード**
 *
 * **金額表示でここが 1.1 を掛けない。** 税込は API が
 * `priceLabel` / `priceIncludingTax` を作って返す（税率の正は
 * `packages/shared`）。画面で掛け算を書くと、税率改定のたびに必ず
 * どこかが古い値のまま残る。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  formatDateTime,
  useCursorPagination,
  type DataTableColumn,
  formatCount,
} from '@mail-sv/ui';
import { Banknote, CreditCard, Download, ExternalLink, Receipt, RotateCcw } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async, ErrorAlert, LoadingBlock } from '../components/async.js';
import { ConfirmTextDialog } from '../components/confirm-text-dialog.js';
import { FormAlert } from '../components/form.js';
import { JobProgressDialog } from '../components/job-progress.js';
import {
  MfaEnrollmentNotice,
  ReauthDialog,
  needsMfaEnrollment,
  needsReauth,
} from '../components/reauth-dialog.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { useAuth, canManageBilling } from '../stores/auth.js';
import { openPendingTab } from '../lib/open-tab.js';
import type {
  BankTransferInfo,
  CancelResult,
  CursorPage,
  Invoice,
  InvoiceDetail,
  InvoicePdfLink,
  JobAccepted,
  SlaView,
  Subscription,
} from '../lib/api-types.js';

export function BillingPage() {
  const { user } = useAuth();
  const org = useOrg();
  const canManage = canManageBilling(user);

  const subscription = useQuery({
    queryKey: qk.subscription(),
    queryFn: () => api.get<Subscription>('/billing/subscription'),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHeader
        title="料金プラン・請求"
        description="ご契約中のプラン、お支払い方法、請求履歴を確認できます。"
        actions={
          canManage ? (
            <Button asChild variant="outline">
              <AppLink to="/billing/plan">プランを変更する</AppLink>
            </Button>
          ) : null
        }
      />

      <div className="space-y-6">
        {!canManage ? (
          <Alert variant="info">
            <AlertTitle>閲覧のみ可能です</AlertTitle>
            <AlertDescription>
              お支払い方法の変更・プラン変更は、契約者（オーナー）のみ行えます。
            </AlertDescription>
          </Alert>
        ) : null}

        <Async query={subscription} lines={5}>
          {(sub) => (
            <div className="space-y-6">
              <TerminationBanner subscription={sub} canManage={canManage} />
              <PendingPriceChangeAlert subscription={sub} />

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between gap-2 text-base">
                      現在のプラン
                      <StatusBadge status={sub.status} dot size="sm" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-2xl font-semibold">{sub.planName}</p>
                      <p className="text-sm text-muted-foreground">
                        {/*
                         * `priceLabel` はサーバが作った「1,650 円（税込・税抜 1,500 円）」。
                         * `billingPeriod` に対応した金額なので、月額固定ではない。
                         */}
                        {sub.price > 0
                          ? `${sub.billingPeriod === 'yearly' ? '年額' : '月額'} ${sub.priceLabel}`
                          : '個別見積り'}
                      </p>
                      {sub.priceGrandfathered ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          ご契約時の価格を据え置いています（現在の料金表は{' '}
                          {formatMoney(sub.catalogPrice, sub.currency)}）。
                        </p>
                      ) : null}
                    </div>
                    <KeyValue
                      items={[
                        { key: '契約期間', value: `${formatDate(sub.currentPeriodStart)} 〜 ${formatDate(sub.currentPeriodEnd)}` },
                        {
                          key: 'お支払い周期',
                          value: sub.billingPeriod === 'yearly' ? '年払い' : '月払い',
                        },
                        {
                          key: '次回請求日',
                          // 解約予約中は次回請求が無いので null が返る。
                          value: sub.nextInvoiceAt ? formatDate(sub.nextInvoiceAt) : 'なし',
                        },
                        {
                          key: '利用中のメールアドレス',
                          value: `${formatCount(org.data?.usage.mailboxes ?? 0)} 件`,
                        },
                        {
                          key: '自動更新',
                          value: sub.cancelAtPeriodEnd ? '停止予定' : '有効',
                        },
                      ]}
                    />
                  </CardContent>
                </Card>

                <PaymentMethodCard subscription={sub} canManage={canManage} />
              </div>

              <UpcomingInvoiceCard subscription={sub} />
              <BillingProfileCard subscription={sub} />
              <SlaSection canManage={canManage} />
              <CancelSection subscription={sub} canManage={canManage} />
            </div>
          )}
        </Async>

        <InvoicesSection />
      </div>
    </>
  );
}

/* ------------------------------------------------------------ 解約の進行状況 */

/**
 * 解約手続きが進行中であることの告知と**撤回**。
 *
 * 以前この画面は「継続をご希望の場合はサポートへご連絡ください」としか
 * 書いておらず、解約は実質**片道切符**だった（撤回の API はあるのに、
 * 呼ぶ画面が無かった）。契約終了まで進むとメールデータが猶予後に
 * 物理削除されるので、削除予定日と撤回導線は必ず同じ場所に出す。
 */
function TerminationBanner({
  subscription,
  canManage,
}: {
  subscription: Subscription;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const term = subscription.termination;

  const revoke = useMutation({
    mutationFn: () => api.post<{ ok: boolean; restoring: boolean }>('/billing/cancel/revoke', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscription() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
    },
  });

  const terminateAfter = term.terminateAfter;
  const terminating = terminateAfter !== null;
  if (!terminating && !subscription.cancelAtPeriodEnd) return null;

  return (
    <Alert variant={terminating ? 'destructive' : 'warning'}>
      <AlertTitle>
        {terminating ? '解約の手続きが進行しています' : '契約期間の終了時に解約されます'}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <ul className="list-disc space-y-1 pl-4 text-sm">
          {terminateAfter !== null ? (
            <>
              <li>
                メールデータ（受信箱・送信箱）の削除予定:{' '}
                <span className="font-medium">{formatDateTime(terminateAfter)}</span>
              </li>
              {term.purgeAfter ? (
                <li>
                  宛先名簿など個人データの消去予定:{' '}
                  <span className="font-medium">{formatDateTime(term.purgeAfter)}</span>
                </li>
              ) : null}
              <li>削除後のデータは復元できません。必要なデータは事前に書き出してください。</li>
            </>
          ) : (
            <>
              <li>
                {formatDate(subscription.currentPeriodEnd)} をもってサービスが停止します（それまでは
                通常どおりご利用いただけます）。
              </li>
              <li>停止後、一定の猶予期間を経てメールデータを削除します（削除予定日はこの画面に表示します）。</li>
            </>
          )}
        </ul>
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => revoke.mutate()} loading={revoke.isPending}>
              <RotateCcw aria-hidden />
              {terminating ? '解約を撤回してサービスを再開する' : '解約を取り消して自動更新を再開する'}
            </Button>
            {revoke.isSuccess ? (
              <span className="text-xs text-success">
                {revoke.data?.restoring
                  ? 'サービスの再開処理を開始しました。'
                  : '解約の予約を取り消しました。'}
              </span>
            ) : null}
          </div>
        ) : null}
        {revoke.isError ? <FormAlert error={revoke.error} /> : null}
      </AlertDescription>
    </Alert>
  );
}

/* ---------------------------------------------------------------- 料金改定 */

/**
 * 予告済みの料金改定。
 *
 * 利用規約は「効力発生日の 30 日前までに通知する」と約束していて通知は
 * 出しているが、**契約者が後から確認できる場所**が無いと
 * 「そんな通知は見ていない」に対して示せるものが通知ログしか無くなる。
 */
function PendingPriceChangeAlert({ subscription }: { subscription: Subscription }) {
  const pending = subscription.pendingPriceChange;
  if (!pending) return null;
  return (
    <Alert variant="info">
      <AlertTitle>料金改定のお知らせ</AlertTitle>
      <AlertDescription>
        {formatDate(pending.effectiveAt)} より、ご契約の料金が {pending.priceLabel} になります。
        {pending.reason ? `（理由: ${pending.reason}）` : null}
      </AlertDescription>
    </Alert>
  );
}

/* -------------------------------------------------------------- 今月の見込み */

/**
 * 当月の請求見込み。
 *
 * 上限を超えた分は従量課金されるのに、契約者は**請求書が届くまで金額を
 * 知る手段が無かった**。金額式は請求書生成と同じ純関数を通した結果を
 * サーバが返しているので、画面は素通しで出す。
 */
function UpcomingInvoiceCard({ subscription }: { subscription: Subscription }) {
  const up = subscription.upcomingInvoice;
  if (!up) return null;
  const currency = subscription.currency;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          今月のご請求予定（{up.yearMonth.slice(0, 4)}年{up.yearMonth.slice(4, 6)}月）
          {up.provisional ? (
            <Badge variant="muted" size="sm">
              確定前の見込み
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {up.transitional ? (
          <Alert variant="info">
            <AlertTitle>今回のご請求は 2 か月分です</AlertTitle>
            <AlertDescription>
              お支払い方法を後払いから前払いへ切り替えるため、この 1 回だけ当月分と翌月分が
              同じ請求書に並びます。翌月以降は通常どおり 1 か月分です。
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {up.lines.map((line, i) => (
                <tr key={`${line.kind}-${i}`} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-3">{line.description}</td>
                  <td className="py-1.5 text-right tabular">{formatMoney(line.amount, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <KeyValue
          items={[
            { key: '小計（税抜）', value: formatMoney(up.subtotal, currency) },
            { key: '消費税', value: formatMoney(up.taxAmount, currency) },
            { key: '合計（税込）', value: formatMoney(up.total, currency) },
          ]}
        />

        <p className="text-xs text-muted-foreground">
          {up.provisional
            ? '当月の使用量が確定していないため、金額は変動します。確定した金額は月次の締め処理後に請求書として発行されます。'
            : '当月分の金額です。請求書は締め処理後に発行されます。'}
        </p>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------- 請求先情報 */

/**
 * 請求先情報（請求書の宛先）の不足告知。
 *
 * `complete:false` のまま月次締めが回ると、**宛名しか無い請求書**が交付され、
 * 経理処理で必ず差し戻される。入力欄はアカウント設定に置いてあるので、
 * ここでは足りない項目を名指しして導線だけ出す。
 */
const BILLING_FIELD_LABEL: Record<string, string> = {
  postalCode: '郵便番号',
  addressLine1: '住所',
  contactName: 'ご担当者名',
};

function BillingProfileCard({ subscription }: { subscription: Subscription }) {
  const profile = subscription.billingProfile;

  return (
    <Section title="請求先情報">
      <Card>
        <CardContent className="space-y-4 p-5">
          {!profile.complete ? (
            <Alert variant="warning">
              <AlertTitle>請求書の宛先が未入力です</AlertTitle>
              <AlertDescription>
                {profile.missing.map((m) => BILLING_FIELD_LABEL[m] ?? m).join('・')}
                が未入力のため、発行する請求書に宛先を記載できません。
              </AlertDescription>
            </Alert>
          ) : null}

          <KeyValue
            items={[
              { key: '宛名', value: profile.displayName },
              { key: '請求書の送付先', value: profile.billingEmail },
              { key: '郵便番号', value: profile.postalCode ?? '未入力' },
              { key: '住所', value: profile.addressLine1 ?? '未入力' },
              { key: 'ご担当者名', value: profile.contactName ?? '未入力' },
              { key: 'お電話番号', value: profile.contactPhone ?? '未入力' },
              {
                key: '適格請求書発行事業者登録番号',
                value: profile.taxId ?? '未登録',
              },
            ]}
          />

          <Button asChild variant="outline" size="sm">
            <AppLink to="/settings">請求先情報を変更する</AppLink>
          </Button>
        </CardContent>
      </Card>
    </Section>
  );
}

/* -------------------------------------------------------------- SLA / クレジット */

/** basis point（9990）を「99.90%」にする。掲示と同じ桁で出す。 */
export function formatBp(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`;
}

/**
 * `yyyyMM` → 「2026年7月」。`lib/time.ts` と同じく**区切りなしの表記が正**。
 *
 * 形が違うときは**加工せずそのまま返す**。以前は無条件に切り出していたため、
 * `yyyy-MM` を渡されると `Number('-0')` が `-0` になって「2026年0月」と
 * 描いていた。`lib/time.ts` の `zonedMonthRange` はその場で throw するのに
 * ここだけが黙って別の月を表示するので、SLA の申告（対象月の翌月末日が期限）
 * で対象月を取り違えても気付けない。読めない値はそのまま見せて、
 * 「表記が違う」ことが画面で分かるようにする。
 */
export function formatYearMonth(ym: string): string {
  if (!/^\d{6}$/.test(ym)) return ym;
  return `${ym.slice(0, 4)}年${Number(ym.slice(4, 6))}月`;
}

/**
 * SLA の掲示とサービスクレジットの申告（`GET /billing/sla` /
 * `POST /billing/sla-claims`）。
 *
 * **掲示（`/legal/sla` 第 5 条）は「対象月の翌月末日までに当社所定の方法で
 * 請求する」と書いているのに、その方法が製品のどこにも無かった。**
 * API は先にあったが、`GET /billing/sla` も `POST /billing/sla-claims` も
 * 呼ぶ画面が 1 枚も無く到達不能だった。
 *
 * **金額は契約者に入力させない**（サーバも受け取らない）。申告は
 * 「どの月に何があったか」だけで、率と金額の確定は運営が行う。
 * 画面でも「申告＝返金の確定ではない」ことを明示する。
 */
function SlaSection({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient();
  const [yearMonth, setYearMonth] = React.useState('');
  const [description, setDescription] = React.useState('');

  const sla = useQuery({
    queryKey: qk.sla(),
    queryFn: () => api.get<SlaView>('/billing/sla'),
    staleTime: 5 * 60_000,
  });

  const claim = useMutation({
    mutationFn: () =>
      api.post<{ id: string; status: string }>('/billing/sla-claims', {
        yearMonth,
        description: description.trim(),
      }),
    onSuccess: () => {
      setDescription('');
      void queryClient.invalidateQueries({ queryKey: qk.sla() });
    },
  });

  const data = sla.data;
  // 取得に失敗しても請求画面そのものは壊さない（SLA は付随情報）。
  if (sla.isError) return null;
  if (!data) return null;

  // 定めが無いプランでは申告の導線を出さない（押せば必ず 400 になる）。
  const hasGuarantee = data.guaranteeBp > 0;
  const months = data.claimableMonths;
  const selected = yearMonth || months[0] || '';
  const canSubmit =
    canManage && hasGuarantee && selected !== '' && description.trim().length >= 10;

  return (
    <Section title="稼働率保証（SLA）">
      <Card>
        <CardContent className="space-y-4 p-5">
          {hasGuarantee ? (
            <>
              <KeyValue
                items={[
                  { key: '保証稼働率', value: `月間 ${formatBp(data.guaranteeBp)}` },
                  {
                    key: '返金の基準',
                    value:
                      data.tiers.length > 0
                        ? data.tiers
                            .map((t) => `${formatBp(t.belowBp)} 未満で ${t.creditBp / 100}%`)
                            .join(' / ')
                        : '—',
                  },
                ]}
              />
              <p className="text-xs text-muted-foreground">
                返金（サービスクレジット）は自動では適用されません。
                <strong>対象月の翌月末日まで</strong>に下のフォームからご申告ください（
                <AppLink to="/legal/sla" className="underline">
                  SLA の掲示
                </AppLink>
                ）。
              </p>
            </>
          ) : (
            <Alert variant="info">
              <AlertTitle>ご利用中のプランに返金の定めはありません</AlertTitle>
              <AlertDescription>
                稼働率は努力目標として掲示しています。返金（サービスクレジット）を含む保証が必要な場合は、
                上位プランへの変更をご検討ください。
              </AlertDescription>
            </Alert>
          )}

          {hasGuarantee && canManage ? (
            <form
              className="space-y-3 rounded-lg border border-border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) claim.mutate();
              }}
            >
              <p className="text-sm font-medium">障害を申告する</p>
              <FormAlert error={claim.error} />
              {claim.isSuccess ? (
                <Alert variant="success">
                  <AlertTitle>申告を受け付けました</AlertTitle>
                  <AlertDescription>
                    運営で稼働率を確認のうえ、返金の可否と金額を確定します。結果は下の一覧に反映されます。
                  </AlertDescription>
                </Alert>
              ) : null}
              {months.length === 0 ? (
                <Alert variant="info">
                  <AlertTitle>いま申告できる対象月はありません</AlertTitle>
                  <AlertDescription>
                    申告の期限は対象月の翌月末日です。期限を過ぎた月はお受けできません。
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Field label="対象の月">
                    <select
                      className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm"
                      value={selected}
                      onChange={(e) => setYearMonth(e.target.value)}
                    >
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {formatYearMonth(m)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="障害の内容"
                    hint="いつ・どのような症状だったかをご記入ください（10 文字以上）。"
                  >
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="例: 7 月 12 日 10:00 頃から約 3 時間、メールの送信が全て失敗しました。"
                    />
                  </Field>
                  <Button type="submit" size="sm" disabled={!canSubmit || claim.isPending}>
                    {claim.isPending ? '送信しています…' : '申告する'}
                  </Button>
                </>
              )}
            </form>
          ) : null}

          {data.credits.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">申告と返金の履歴</p>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {data.credits.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
                    <StatusBadge status={c.status} size="sm" dot />
                    <span className="tabular">
                      {formatDate(c.periodStart)} 〜 {formatDate(c.periodEnd)}
                    </span>
                    <span className="tabular">
                      {c.amount > 0 ? formatMoney(c.amount) : '金額は確認中'}
                    </span>
                    {c.remainingAmount > 0 ? (
                      <Badge variant="muted" size="sm">
                        次回へ繰り越し {formatMoney(c.remainingAmount)}
                      </Badge>
                    ) : null}
                    {c.applied ? (
                      <Badge variant="muted" size="sm">
                        請求書に反映済み
                      </Badge>
                    ) : null}
                    {c.reason ? (
                      <span className="w-full text-xs text-muted-foreground">{c.reason}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </Section>
  );
}

/* ------------------------------------------------------------------ 解約 */

/**
 * 解約。**特商法表記の「コントロールパネルからいつでも解約できる」の実体。**
 *
 * API はパスで意味を分けている（`cancelAtPeriodEnd` の値では分けない）:
 *   - `POST /billing/cancel`           … 期間末解約の予約
 *   - `POST /billing/cancel/immediate` … 即時解約（fresh-auth 必須）
 *   - `POST /billing/cancel/revoke`    … 撤回
 * 「撤回のつもりで false を送ったら即時解約された」を構造的に潰した形なので、
 * 画面もこの 3 本をそのまま使う（1 本に寄せ直さないこと）。
 */
function CancelSection({
  subscription,
  canManage,
}: {
  subscription: Subscription;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [immediate, setImmediate] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [reauthOpen, setReauthOpen] = React.useState(false);

  const cancel = useMutation({
    mutationFn: (opts: { immediate: boolean }) =>
      api.post<CancelResult>(
        opts.immediate ? '/billing/cancel/immediate' : '/billing/cancel',
        { reason: reason.trim() || undefined },
      ),
    onSuccess: () => {
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: qk.subscription() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
    },
    onError: (error) => {
      // 再認証で解けるものはダイアログへ送る（画面を行き止まりにしない）。
      if (needsReauth(error)) {
        setConfirmOpen(false);
        setReauthOpen(true);
      }
    },
  });

  // 既に解約手続き中なら、ここは出さない（撤回は `TerminationBanner` が持つ）。
  if (subscription.cancelAtPeriodEnd || subscription.termination.terminateAfter) return null;

  return (
    <Section title="解約">
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>
              ご契約はこの画面からいつでも解約できます。解約後の取り扱いは
              <AppLink
                to="/legal/tokushoho"
                className="mx-1 text-primary underline-offset-4 hover:underline"
              >
                特定商取引法に基づく表記
              </AppLink>
              をご確認ください。
            </p>
            <p>
              期間途中で解約した場合も、日割りでの返金は行っておりません（利用規約に定めのとおり）。
            </p>
          </div>

          {!canManage ? (
            <Alert variant="info">
              <AlertTitle>解約できるのは契約者（オーナー）のみです</AlertTitle>
              <AlertDescription>
                お手続きが必要な場合は、組織の契約者にご依頼ください。
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {needsMfaEnrollment(cancel.error) ? (
                <MfaEnrollmentNotice purpose="即時解約" />
              ) : cancel.isError && !needsReauth(cancel.error) ? (
                <FormAlert error={cancel.error} />
              ) : null}

              <Field
                label="解約の理由"
                htmlFor="cancel-reason"
                hint="今後のサービス改善のために伺っています（そのまま契約履歴に記録します）。"
              >
                <Textarea
                  id="cancel-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="例: 事業の統合により別のサービスへ移行するため"
                />
              </Field>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">解約の時期</legend>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    className="mt-1"
                    name="cancel-timing"
                    checked={!immediate}
                    onChange={() => setImmediate(false)}
                  />
                  <span>
                    契約期間の終了時（{formatDate(subscription.currentPeriodEnd)}）に解約する
                    <span className="block text-xs text-muted-foreground">
                      それまでは通常どおりご利用いただけます。おすすめの方法です。
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    className="mt-1"
                    name="cancel-timing"
                    checked={immediate}
                    onChange={() => setImmediate(true)}
                  />
                  <span>
                    今すぐ解約する
                    <span className="block text-xs text-muted-foreground">
                      すぐにメールの送受信ができなくなります。本人確認（2 段階認証）が必要です。
                    </span>
                  </span>
                </label>
              </fieldset>

              <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
                解約の手続きに進む
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmTextDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={immediate ? '今すぐ解約します' : '契約期間の終了時に解約します'}
        description="お手続き後も、この画面から解約を撤回できます。"
        confirmText="解約する"
        confirmLabel="解約する"
        pending={cancel.isPending}
        error={cancel.isError && !needsReauth(cancel.error) ? cancel.error : undefined}
        consequences={
          immediate
            ? [
                'メールの送受信がただちに停止します。',
                '一定の猶予期間ののち、保存されているメールデータを削除します（削除予定日はこの画面に表示します）。',
                '期間途中の解約でも、日割りでの返金は行いません。',
              ]
            : [
                `${formatDate(subscription.currentPeriodEnd)} をもってメールの送受信が停止します。`,
                '停止後、一定の猶予期間ののちに保存されているメールデータを削除します。',
                '次回以降の請求は行われません。',
              ]
        }
        onConfirm={() => cancel.mutate({ immediate })}
      />

      <ReauthDialog
        open={reauthOpen}
        onOpenChange={setReauthOpen}
        purpose="解約のお手続き"
        onSuccess={() => cancel.mutate({ immediate })}
      />
    </Section>
  );
}

/**
 * 振込先の案内。
 *
 * **null は「振込先が無い」ではなく「まだ設定されていない」。**
 * 空欄で済ませると、契約者は請求書が来ても払えない。運営が
 * `INVOICE_BANK_INFO` を入れるまでは問い合わせ導線を出す。
 */
function BankTransferBox({ info }: { info: BankTransferInfo | null }) {
  if (!info) {
    return (
      <Alert variant="warning">
        <AlertTitle>お振込先の案内を準備中です</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            お手数ですが、サポート窓口までお問い合わせください。請求書の発行後に個別にご案内します。
          </p>
          {/*
           * **窓口へのリンクを必ず添える。** 銀行振込が唯一動く支払い手段の
           * 環境で振込先が未設定だと、契約者には「払う方法が無い」状態に
           * なる。文面だけ出して導線が無いと、督促だけが自動で進む。
           */}
          <Button asChild variant="outline" size="sm">
            <AppLink to="/support">サポートへ問い合わせる</AppLink>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-panel p-4">
      <p className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Banknote className="size-4 text-muted-foreground" aria-hidden />
        お振込先
      </p>
      <ul className="space-y-0.5 text-sm">
        {info.lines.map((line, i) => (
          <li key={i} className="font-mono">
            {line}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">{info.note}</p>
    </div>
  );
}

/**
 * お支払い方法。
 *
 * **この環境で使える方法しか出さない。** `billingProvider` が `stripe` 以外の
 * ときにカード登録ボタンを出すと、押しても必ず 400 になる
 * （`POST /billing/payment-method/session` がその場で弾く）。
 * 押しても何も起きないボタンを画面に残さないため、`manual` のときは
 * 請求書払い（銀行振込）の登録だけを出す。
 */
function PaymentMethodCard({
  subscription,
  canManage,
}: {
  subscription: Subscription;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = React.useState(false);

  /**
   * カード（Stripe）の登録セッションは **API が同期で URL を返さない**。
   * 決済事業者への問い合わせは worker（`billing` キュー）の担当で、API は
   * ジョブを受理して `{jobId}` を返すだけ（コントロールパネル全体の応答時間を
   * 外部 API の遅延に引きずられないようにするため）。
   */
  const session = useMutation({
    mutationFn: () => api.post<JobAccepted>('/billing/payment-method/session'),
    onSuccess: (res) => setJobId(res.jobId),
    onError: (error) => {
      if (needsReauth(error)) setReauthOpen(true);
    },
  });

  /** 請求書払い（銀行振込）の選択。fresh-auth は要らない。 */
  const registerInvoice = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/billing/payment-method', { kind: 'invoice' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.subscription() }),
  });

  const pm = subscription.paymentMethod;
  const stripe = subscription.billingProvider === 'stripe';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">お支払い方法</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pm.kind === 'card' ? (
          <div className="flex items-center gap-3">
            <CreditCard className="size-5 text-muted-foreground" aria-hidden />
            <div>
              <p className="text-sm font-medium">
                {pm.brand ?? 'カード'} •••• {pm.last4 ?? '----'}
              </p>
              <p className="text-xs text-muted-foreground">
                有効期限 {pm.expMonth ?? '--'} / {pm.expYear ?? '----'}
              </p>
            </div>
          </div>
        ) : pm.kind === 'invoice' ? (
          <p className="text-sm">請求書によるお支払い（銀行振込）</p>
        ) : (
          <Alert variant="warning">
            <AlertTitle>お支払い方法が未登録です</AlertTitle>
            <AlertDescription>
              登録がない場合、無料期間の終了後に送信が停止します（受信は停止しません）。
            </AlertDescription>
          </Alert>
        )}

        {/* 銀行振込の案内は、請求書払いを選ぶ環境では常設する。 */}
        {(!stripe || pm.kind === 'invoice') ? (
          <BankTransferBox info={subscription.bankTransferInfo} />
        ) : null}

        {canManage ? (
          <div className="flex flex-wrap gap-2">
            {stripe ? (
              <Button variant="outline" onClick={() => session.mutate()} loading={session.isPending}>
                {pm.kind === 'card' ? 'カードを変更する' : 'カードを登録する'}
                <ExternalLink aria-hidden />
              </Button>
            ) : null}
            {pm.kind !== 'invoice' ? (
              <Button
                variant="outline"
                onClick={() => registerInvoice.mutate()}
                loading={registerInvoice.isPending}
              >
                <Banknote aria-hidden />
                請求書払い（銀行振込）にする
              </Button>
            ) : null}
          </div>
        ) : null}

        {needsMfaEnrollment(session.error) ? (
          <MfaEnrollmentNotice purpose="お支払い方法の登録" />
        ) : session.isError && !needsReauth(session.error) ? (
          <ErrorAlert error={session.error} />
        ) : null}
        {registerInvoice.isError ? <ErrorAlert error={registerInvoice.error} /> : null}

        <p className="text-xs text-muted-foreground">
          {stripe
            ? 'カード情報は決済代行会社のページで入力していただきます。当サービスのサーバーには保存されません。'
            : 'この環境でのお支払いは、請求書によるお振込のみに対応しています。'}
        </p>
      </CardContent>

      <JobProgressDialog
        open={jobId !== null}
        onOpenChange={(open) => {
          if (!open) setJobId(null);
        }}
        title="お支払い方法の登録画面を準備しています"
        jobId={jobId}
        onDone={() => queryClient.invalidateQueries({ queryKey: qk.subscription() })}
      />

      <ReauthDialog
        open={reauthOpen}
        onOpenChange={setReauthOpen}
        purpose="お支払い方法の登録"
        onSuccess={() => session.mutate()}
      />
    </Card>
  );
}

function InvoicesSection() {
  const page = useCursorPagination(20);
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [downloadError, setDownloadError] = React.useState<unknown>(null);
  /** 明細ダイアログで開いている請求書。PDF が無くても内訳は読める。 */
  const [detailId, setDetailId] = React.useState<string | null>(null);

  const list = useQuery({
    queryKey: qk.invoices({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<Invoice> & { bankTransferInfo: BankTransferInfo | null }>(
        `/billing/invoices?${params.toString()}`,
      );
    },
  });

  /**
   * 請求書 PDF の取得。
   *
   * `GET /portal/invoices/:id/pdf` は **PDF 本体を返さない**。返すのは
   * 署名付き URL（`{url, expiresAt, objectKey, number, bucket}`）で、
   * 実体は非公開バケットにある。レスポンスをそのまま Blob 保存すると
   * 中身が JSON の `.pdf` が出来上がるので、**必ず `url` へ遷移させる**。
   *
   * ポップアップブロック対策として、`window.open` は
   * **クリックと同じイベントループで**空タブを開き、URL が得られてから
   * `location` を差し替える（Webメールの導線と同じ作法）。
   */
  const download = React.useCallback(async (invoice: Invoice) => {
    setDownloading(invoice.id);
    setDownloadError(null);
    const tab = openPendingTab();
    try {
      const link = await api.get<InvoicePdfLink>(
        `/invoices/${encodeURIComponent(invoice.id)}/pdf`,
      );
      tab.navigate(link.url);
    } catch (e) {
      tab.close();
      setDownloadError(e);
    } finally {
      setDownloading(null);
    }
  }, []);

  const columns: Array<DataTableColumn<Invoice>> = [
    {
      id: 'period',
      header: '請求期間',
      cell: (row) => (
        <div>
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {row.number}
            {/* 赤伝（適格返還請求書）は請求ではない。並べたままだと二重請求に見える。 */}
            {row.isCreditNote ? (
              <Badge variant="muted" size="sm">
                返還請求書
              </Badge>
            ) : null}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDate(row.periodStart)} 〜 {formatDate(row.periodEnd)}
          </p>
        </div>
      ),
    },
    {
      id: 'total',
      header: '金額',
      width: 'w-32',
      align: 'right',
      cell: (row) => formatMoney(row.total, row.currency),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-28',
      cell: (row) => (
        <div className="space-y-1">
          <StatusBadge status={row.status} size="sm" dot />
          {row.status === 'open' && row.dueAt ? (
            <p className="text-[11px] text-muted-foreground">期限 {formatDate(row.dueAt)}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'pdf',
      header: '',
      width: 'w-44',
      align: 'right',
      /*
       * **明細をいつでも読めるようにする。** PDF の生成は運営側の env
       * （発行者名・住所・バケット）が揃うまで動かないので、それまで
       * 契約者は合計額しか見られなかった。明細（`GET /invoices/:id`）は
       * env に依存しないので、こちらは常に開ける。
       */
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setDetailId(row.id)}>
            <Receipt aria-hidden />
            明細
          </Button>
          {row.hasPdf ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void download(row)}
              loading={downloading === row.id}
            >
              <Download aria-hidden />
              PDF
            </Button>
          ) : (
            <span
              className="text-xs text-muted-foreground"
              title="請求書 PDF は準備中です。明細はこの画面で確認できます。"
            >
              PDF 準備中
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <Section title="請求履歴">
      {downloadError ? <ErrorAlert error={downloadError} /> : null}
      {list.isPending ? (
        <LoadingBlock lines={3} />
      ) : (
        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.isError ? '請求履歴を取得できませんでした。' : undefined}
          emptyTitle="請求履歴はまだありません"
          emptyDescription="最初の請求が確定すると、ここに表示されます。"
          footer={
            <CursorPagination
              nextCursor={list.data?.nextCursor ?? null}
              canGoPrev={page.canGoPrev}
              onNext={() => page.goNext(list.data?.nextCursor ?? null)}
              onPrev={page.goPrev}
              limit={page.limit}
              onLimitChange={page.setLimit}
              currentCount={list.data?.items.length}
              loading={list.isFetching}
            />
          }
        />
      )}
      {/*
       * **振込先は請求履歴と同じ画面に常設する。** 以前は請求書 PDF にしか
       * 載っておらず、PDF の生成に必要な env が揃うまで契約者が振込先を
       * 知る手段がまったく無かった（＝払えないのと同義）。
       */}
      <BankTransferBox info={list.data?.bankTransferInfo ?? null} />
      <p className="text-xs text-muted-foreground">
        <Badge variant="muted" size="sm">
          ご注意
        </Badge>{' '}
        お支払いが確認できない場合でも、メールの受信は停止しません（送信のみ停止します）。
      </p>

      {detailId ? (
        <InvoiceDetailDialog invoiceId={detailId} onClose={() => setDetailId(null)} />
      ) : null}
    </Section>
  );
}

/**
 * 請求書 1 通の明細（`GET /portal/invoices/:invoiceId`）。
 *
 * **これが無いあいだ、契約者は「何にいくら課金されたか」を確認できなかった。**
 * 内訳を読む唯一の経路が PDF で、その生成は運営側の env
 * （`INVOICE_ISSUER_NAME` / `INVOICE_ISSUER_ADDRESS` / `S3_BUCKET_INVOICES`）が
 * 揃うまで動かない。既定構成では PDF が存在せず、押すと 404 が返るだけだった。
 *
 * 金額は**サーバが計算した値をそのまま出す**。税額はインボイス制度の要件で
 * 「税率ごとに 1 回だけ」丸めており（`packages/shared/src/billing-math.ts`）、
 * 画面で掛け算し直すと小計 + 消費税 ≠ 合計の表示になる。
 */
function InvoiceDetailDialog({
  invoiceId,
  onClose,
}: {
  invoiceId: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: qk.invoice(invoiceId),
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${encodeURIComponent(invoiceId)}`),
  });

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>請求の内訳</DialogTitle>
          <DialogDescription>
            {detail.data ? `${detail.data.number}` : '読み込んでいます…'}
          </DialogDescription>
        </DialogHeader>

        <Async query={detail} lines={5}>
          {(data) => (
            <div className="space-y-4">
              <KeyValue
                columns={2}
                items={[
                  {
                    key: '請求期間',
                    value: `${formatDate(data.periodStart)} 〜 ${formatDate(data.periodEnd)}`,
                  },
                  { key: '状態', value: <StatusBadge status={data.status} size="sm" dot /> },
                  { key: '発行日', value: data.issuedAt ? formatDate(data.issuedAt) : '—' },
                  { key: 'お支払い期限', value: data.dueAt ? formatDate(data.dueAt) : '—' },
                ]}
              />

              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">内容</TableHead>
                      <TableHead scope="col" className="w-20 text-right">
                        数量
                      </TableHead>
                      <TableHead scope="col" className="w-28 text-right">
                        単価
                      </TableHead>
                      <TableHead scope="col" className="w-28 text-right">
                        金額
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.lines.map((l) => (
                      <TableRow key={l.lineNo}>
                        <TableCell className="text-sm">{l.description}</TableCell>
                        <TableCell className="tabular text-right text-sm">{l.quantity}</TableCell>
                        <TableCell className="tabular text-right text-sm">
                          {formatMoney(l.unitPrice, data.currency)}
                        </TableCell>
                        <TableCell className="tabular text-right text-sm">
                          {formatMoney(l.amount, data.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <dl className="ml-auto max-w-xs space-y-1 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">小計（税抜）</dt>
                  <dd className="tabular">{formatMoney(data.subtotal, data.currency)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">
                    消費税{data.taxRateBp ? `（${data.taxRateBp / 100}%）` : ''}
                  </dt>
                  <dd className="tabular">{formatMoney(data.taxAmount, data.currency)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-border pt-1 font-medium">
                  <dt>合計</dt>
                  <dd className="tabular">{formatMoney(data.total, data.currency)}</dd>
                </div>
              </dl>

              {/*
               * **登録番号が無ければ適格請求書ではない。** 黙って出すと
               * 受け取った側が仕入税額控除に使えると誤解する。
               */}
              {data.issuerRegistrationNo ? (
                <p className="text-xs text-muted-foreground">
                  適格請求書発行事業者登録番号: {data.issuerRegistrationNo}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  この請求は適格請求書（インボイス）ではありません。
                </p>
              )}
            </div>
          )}
        </Async>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
